import {
  Button,
  Modal,
  Select,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Col, Form, Row } from 'reactstrap';
import { SemesterCourse } from '../../../api/requests/curriculum';
import useTextValidationType, {
  FormData,
} from '../hooks/useTextValidationType';
import {
  defaultValues,
  transformValidationType,
} from '../hooks/useTransformValidationType';
import FileDownload from './FileDownload';

interface Props {}

export interface ModalValidationTypeInfoRef {
  openModal: (course: SemesterCourse | null) => void;
  closeModal: () => void;
}

const ModalValidationTypeInfo = forwardRef<ModalValidationTypeInfoRef, Props>(
  (_, ref) => {
    const { texts, validationOptions, rapOptions } = useTextValidationType();
    const { watch, control, reset } = useForm<FormData>({
      defaultValues,
    });

    const [modal, setModal] = useState(false);

    const openModal = useCallback(
      (course: SemesterCourse | null) => {
        reset({
          ...transformValidationType({
            course,
            options: {
              validationOptions,
              rapOptions,
            },
          }),
        });
        setModal(true);
      },
      [reset, validationOptions, rapOptions],
    );

    const closeModal = useCallback(() => {
      reset({ ...defaultValues });
      setModal(false);
    }, [reset]);

    useImperativeHandle(ref, () => ({
      openModal: openModal,
      closeModal: closeModal,
    }));

    const [watchValidation, watchRapOption, supplementaryFiles] = watch([
      'validationType',
      'rapOption',
      'supplementaryFiles',
    ]);
    const selectedValidation = watchValidation?.value;
    const selectedRapOption = watchRapOption?.value;
    const isProficiency = selectedRapOption === 'proficiency';

    return (
      <Modal
        isOpen={modal}
        toggle={closeModal}
        closeOnBackdrop={true}
        size="lg"
      >
        <Form>
          <div className="text-black fs-22 text-center">{texts.title}</div>
          <Row className="mb-3">
            <Col xs={12}>
              <TextInput
                name="courseName"
                label={texts.courseNameLabel}
                control={control}
                disabled
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12}>
              <Select
                name="validationType"
                label={texts.validationType.selectLabel}
                options={validationOptions}
                control={control}
                disabled
              />
            </Col>
          </Row>

          {selectedValidation === 'internal' && (
            <Row className="mb-3">
              <Col xs={12}>
                <TextInput
                  name="code"
                  label={texts.courseCodeLabel}
                  control={control}
                  disabled
                />
              </Col>
            </Row>
          )}

          {selectedValidation === 'rap' && (
            <Row className="mb-3">
              <Col xs={12}>
                <Select
                  name="rapOption"
                  label={texts.rapType.selectLabel}
                  options={rapOptions}
                  control={control}
                  disabled
                />
              </Col>
            </Row>
          )}

          {!!(selectedValidation === 'rap' && selectedRapOption) && (
            <Row className="mb-3">
              <Col xs={12} md={isProficiency ? 6 : 12}>
                <TextInput
                  name="courseGrade"
                  label={texts.courseGradeLabel}
                  control={control}
                  disabled
                />
              </Col>
              {isProficiency && (
                <Col xs={12} md={6}>
                  <TextInput
                    name="examGrade"
                    label={texts.examGradeLable}
                    control={control}
                    disabled
                  />
                </Col>
              )}
              <Col xs={12}>
                <TextAreaInput
                  label={texts.description.label}
                  name="description"
                  placeholder={texts.description.placeholder}
                  control={control}
                  disabled
                />
              </Col>
            </Row>
          )}

          {selectedValidation === 'external' && (
            <Row className="mb-3">
              <Col xs={12} md={6}>
                <TextInput
                  name="externalCourseName"
                  label={texts.externalCourseName.label}
                  control={control}
                  disabled
                />
              </Col>
              <Col xs={12} md={6}>
                <TextInput
                  name="institution"
                  label={texts.institution.label}
                  control={control}
                  disabled
                />
              </Col>
              <Col xs={12} md={6}>
                <TextInput
                  name="courseGrade"
                  label={texts.courseGradeLabel}
                  tooltip={texts.courseGradeForExternal.tooltip}
                  control={control}
                  disabled
                />
              </Col>
              <Col xs={12} md={6}>
                <TextInput
                  name="externalGrade"
                  label={texts.externalGrade.label}
                  tooltip={texts.externalGrade.tooltip}
                  control={control}
                  disabled
                />
              </Col>
              <Col xs={12}>
                <TextAreaInput
                  label={texts.description.label}
                  name="description"
                  placeholder={texts.description.placeholder}
                  control={control}
                  disabled
                />
              </Col>
            </Row>
          )}

          <Row>
            <Col xs={12} className="py-4">
              {supplementaryFiles?.map((doc, i) => (
                <FileDownload
                  key={i.toString()}
                  text={texts.dowmnloadFile.button}
                  label={i === 0 ? texts.dowmnloadFile.label : ''}
                  file={{
                    ...doc,
                    name: `${doc.name}.${doc.extension}`,
                  }}
                />
              ))}
            </Col>
            <Col xs={12}>
              <Button
                onClick={closeModal}
                text={texts.actions.close}
                outlined
                fullwidth
              />
            </Col>
          </Row>
        </Form>
      </Modal>
    );
  },
);

export default ModalValidationTypeInfo;
